const POSITION_TOP_CENTER = 'top-center';
const POSITION_TOP_RIGHT = 'top-right';
const POSITION_RIGHT_CENTER = 'right-center';
const POSITION_BOTTOM_RIGHT = 'bottom-right';
const POSITION_BOTTOM_CENTER = 'bottom-center';
const POSITION_BOTTOM_LEFT = 'bottom-left';
const POSITION_LEFT_CENTER = 'left-center';
const POSITION_TOP_LEFT = 'top-left';

// collect positions in original priority order
export const positions = [
    POSITION_BOTTOM_RIGHT,
    POSITION_RIGHT_CENTER,
    POSITION_TOP_RIGHT,
    POSITION_TOP_CENTER,
    POSITION_TOP_LEFT,
    POSITION_LEFT_CENTER,
    POSITION_BOTTOM_LEFT,
    POSITION_BOTTOM_CENTER,
];

const MdsPositioning = {
    methods: {
        /**********************************************
        Main methods
        **********************************************/

        getPositionAttributes(spaceFromTrigger = 6) {
            const attributes = {};
            const sizes = this.getComponentTriggerSizes();
            let setPosition = this.position[0];
            if (this.autoPosition) {
                setPosition = this.findFirstViablePosition(sizes.component, sizes.trigger, sizes.window, this.position);
            }
            const heightBorders = this.variation === 'default' && setPosition.split('-')[0] === 'top' ? -2 : 0;
            const widthBorders = this.variation === 'default' && setPosition.split('-')[0] === 'left' ? -2 : 0;

            // check if trigger sizes are available
            if (sizes.trigger !== {}) {
                let { topPos, leftPos } = this.getTopLeftPosition(
                    sizes.component,
                    sizes.trigger,
                    setPosition,
                    spaceFromTrigger
                );

                topPos += heightBorders;
                leftPos += widthBorders;

                if (!this.fixed) {
                    topPos += sizes.window.pageYOffset;
                    leftPos += sizes.window.pageXOffset;
                }

                attributes.top = topPos;
                attributes.left = leftPos;
            }

            return { setPosition, attributes };
        },

        /**********************************************
        Define top and left positions
        **********************************************/

        getTopLeftPosition(componentSize, triggerSize, newPosition, spaceFromTrigger) {
            let topPos;
            let leftPos;
            // determine trigger centers
            const triggerCenterHorizontal = triggerSize.left + 0.5 * triggerSize.width;
            const triggerCenterVertical = triggerSize.top + 0.5 * triggerSize.height;

            // get top position
            switch (newPosition) {
                case POSITION_TOP_CENTER:
                case POSITION_TOP_RIGHT:
                case POSITION_TOP_LEFT:
                    topPos = triggerSize.top - (componentSize.height + spaceFromTrigger);
                    break;
                case POSITION_BOTTOM_CENTER:
                case POSITION_BOTTOM_RIGHT:
                case POSITION_BOTTOM_LEFT:
                    topPos = triggerSize.bottom + spaceFromTrigger;
                    break;
                case POSITION_LEFT_CENTER:
                case POSITION_RIGHT_CENTER:
                    topPos = triggerCenterVertical - 0.5 * componentSize.height;
                    break;
            }

            // get left position
            switch (newPosition) {
                case POSITION_TOP_CENTER:
                case POSITION_BOTTOM_CENTER:
                    leftPos = triggerCenterHorizontal - 0.5 * componentSize.width;
                    break;
                case POSITION_TOP_RIGHT:
                case POSITION_BOTTOM_RIGHT:
                    leftPos = triggerSize.left;
                    break;
                case POSITION_TOP_LEFT:
                case POSITION_BOTTOM_LEFT:
                    leftPos = triggerSize.right - componentSize.width;
                    break;
                case POSITION_RIGHT_CENTER:
                    leftPos = triggerSize.right + spaceFromTrigger;
                    break;
                case POSITION_LEFT_CENTER:
                    leftPos = triggerSize.left - (componentSize.width + spaceFromTrigger);
                    break;
            }

            return { topPos, leftPos };
        },

        /**********************************************
        Helper functions
        **********************************************/

        // get size and its position relative to the viewport for component and trigger
        getComponentTriggerSizes(objWindow = window) {
            const windowSize = (({ innerHeight, innerWidth, pageXOffset, pageYOffset }) => ({
                innerHeight,
                innerWidth,
                pageXOffset,
                pageYOffset,
            }))(objWindow);

            const componentSize = this.$el.getBoundingClientRect();

            const triggerSize =
                this.triggeredBy && document.getElementById(`${this.triggeredBy}`)
                    ? document.getElementById(`${this.triggeredBy}`).getBoundingClientRect()
                    : {};
            return { window: windowSize, component: componentSize, trigger: triggerSize };
        },

        // Apply `style` attribute to component
        formatPositionStyleAttributes(attributes) {
            const top = attributes.top ? `top: ${attributes.top}px; ` : '';
            const left = attributes.left ? `left: ${attributes.left}px; ` : '';

            return `${top}${left}`.trim();
        },

        /**********************************************
        Helper functions for auto positioning
        **********************************************/
        // determine positions where component can be displayed
        // returns array of viable positions
        findViablePositions(componentSize, triggerSize, windowSize) {
            // determine trigger centers
            const triggerCenterHorizontal = triggerSize.left + 0.5 * triggerSize.width;
            const triggerCenterVertical = triggerSize.top + 0.5 * triggerSize.height;
            // determine component center height and width
            const componentHalfHeight = 0.5 * (componentSize.height + 2);
            const componentHalfWidth = 0.5 * componentSize.width;

            // determine if component fits on top, bottom, left, or right of trigger
            const fitsTop = triggerSize.top >= componentSize.height;
            const fitsBottom = windowSize.innerHeight - triggerSize.bottom >= componentSize.height;
            const fitsLeft = triggerSize.left - componentSize.width > 0;
            const fitsRight = windowSize.innerWidth > componentSize.width + triggerSize.right;
            const fitsCenter =
                triggerCenterHorizontal - componentHalfWidth > 0 &&
                triggerCenterHorizontal + componentHalfWidth < windowSize.innerWidth;

            const viablePositions = {};
            viablePositions[POSITION_TOP_CENTER] = fitsTop && fitsCenter;

            viablePositions[POSITION_TOP_RIGHT] =
                fitsTop && triggerSize.left + componentSize.width < windowSize.innerWidth;

            viablePositions[POSITION_RIGHT_CENTER] =
                fitsRight &&
                triggerCenterVertical - componentHalfHeight > 0 &&
                triggerCenterVertical + componentHalfHeight < windowSize.innerHeight;

            viablePositions[POSITION_BOTTOM_RIGHT] =
                fitsBottom &&
                triggerSize.left > 0 &&
                triggerCenterHorizontal + componentSize.width < windowSize.innerWidth;

            viablePositions[POSITION_BOTTOM_CENTER] = fitsBottom && fitsCenter;

            viablePositions[POSITION_BOTTOM_LEFT] =
                fitsBottom &&
                triggerCenterHorizontal < windowSize.innerWidth &&
                triggerCenterHorizontal - componentSize.width > 0;

            viablePositions[POSITION_LEFT_CENTER] =
                fitsLeft &&
                triggerCenterVertical - componentHalfHeight > 0 &&
                triggerCenterVertical + componentHalfHeight < windowSize.innerHeight;

            viablePositions[POSITION_TOP_LEFT] =
                fitsTop && triggerSize.right < windowSize.innerWidth && triggerSize.right - componentSize.width > 0;

            return viablePositions;
        },

        // determines best positions from viable positions
        findFirstViablePosition(componentSize, triggerSize, windowSize, sortedPositions) {
            const viablePositions = this.findViablePositions(componentSize, triggerSize, windowSize);
            let foundPosition = sortedPositions[0];

            for (let i = 0; i < sortedPositions.length; i++) {
                if (viablePositions[sortedPositions[i]]) {
                    foundPosition = sortedPositions[i];
                    break;
                }
            }

            return foundPosition;
        },

        debounce(func, wait, immediate) {
            let timeout;
            return function() {
                const context = this;
                const args = arguments;
                const later = () => {
                    timeout = null;
                    if (!immediate) {
                        func.apply(context, args);
                    }
                };
                const callNow = immediate && !timeout;
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
                if (callNow) {
                    func.apply(context, args);
                }
            };
        },
    },
};

export default MdsPositioning;
